import {
  W, H, CONTAINER_LEFT, CONTAINER_RIGHT, CONTAINER_TOP, CONTAINER_BOTTOM,
  DEATH_LINE_Y, DROPPER_Y, WALL_THICK, GamePhase, Button, Effect, FruitData,
} from './types';
import { FRUITS, fruitDef, fruitRadius, spriteFor } from './fruits';
import { fruitBodies } from './physics';

export interface Scene {
  phase: GamePhase;
  score: number;
  best: number;
  dropperX: number;
  currentTier: number;
  nextTier: number;
  effects: Effect[];
  shakeX: number;
  shakeY: number;
  overlayStartMs: number;   // when the current overlay (game over) appeared, for fade-in
  now: number;
}

interface Theme {
  bgTop: string; bgBottom: string;
  container: string; containerIn: string;
  accent: string; text: string; textDim: string;
  death: string; flash: string; gold: string; overlayBg: string;
  onAccent: string; overlayText: string;
}

let theme: Theme;
let bgGrad: CanvasGradient;

export function getGold(): string { return theme.gold; }

export function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  theme = {
    bgTop: v('--bg-top'), bgBottom: v('--bg-bottom'),
    container: v('--container'), containerIn: v('--container-in'),
    accent: v('--accent'), text: v('--text'), textDim: v('--text-dim'),
    death: v('--death'), flash: v('--flash'), gold: v('--gold'),
    overlayBg: v('--overlay-bg'),
    onAccent: v('--on-accent'), overlayText: v('--overlay-text'),
  };
  bgGrad = ctx.createLinearGradient(0, 0, 0, H);
  bgGrad.addColorStop(0, theme.bgTop);
  bgGrad.addColorStop(1, theme.bgBottom);
  return ctx;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ── Button geometry (shared with input for hit-testing) ──────────────────────
const PAUSE_BTN: Button = { x: W - 52, y: 14, w: 38, h: 38 };

export function getButtons(phase: GamePhase): Record<string, Button> {
  const cx = W / 2;
  switch (phase) {
    case 'MENU':
      return { play: { x: cx - 90, y: 470, w: 180, h: 58 } };
    case 'PLAYING':
      return { pause: PAUSE_BTN };
    case 'PAUSED':
      return {
        resume:  { x: cx - 90, y: 320, w: 180, h: 52 },
        restart: { x: cx - 90, y: 388, w: 180, h: 52 },
        home:    { x: cx - 90, y: 456, w: 180, h: 52 },
      };
    case 'GAME_OVER':
      return {
        again: { x: cx - 90, y: 396, w: 180, h: 52 },
        menu:  { x: cx - 90, y: 464, w: 180, h: 52 },
      };
  }
}

// ── Fruit drawing ────────────────────────────────────────────────────────────
function easeOut(t: number): number { return 1 - (1 - t) * (1 - t); }
function easeOutBack(t: number): number {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function fruitScale(age: number, scaleFrom: number): number {
  if (scaleFrom >= 1) return 1;
  if (age <= 0) return scaleFrom;          // guard against clock skew (negative age)
  const merged = scaleFrom < 0.7;          // merged fruits pop with overshoot
  const dur = merged ? 200 : 80;
  if (age >= dur) return 1;
  const t = age / dur;
  const e = merged ? easeOutBack(t) : easeOut(t);
  return scaleFrom + (1 - scaleFrom) * e;
}

function drawFruit(
  ctx: CanvasRenderingContext2D,
  tier: number, x: number, y: number, angle: number, scale: number, alpha = 1,
): void {
  const r = fruitRadius(tier);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(scale, scale);
  const sprite = spriteFor(tier);
  if (sprite && sprite.complete && sprite.naturalWidth) {
    // Fit sprite width to the body diameter; vertical overflow (stems) is fine.
    const d = r * 2;
    const dw = d;
    const dh = d * (sprite.naturalHeight / sprite.naturalWidth);
    ctx.drawImage(sprite, -dw / 2, -dh / 2, dw, dh);
  } else {
    ctx.fillStyle = fruitDef(tier).color;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// ── Effects ──────────────────────────────────────────────────────────────────
function drawEffects(ctx: CanvasRenderingContext2D, effects: Effect[], now: number): void {
  for (const e of effects) {
    const t = Math.max(0, Math.min((now - e.startMs) / e.dur, 1));
    ctx.save();
    if (e.kind === 'flash') {
      ctx.globalAlpha = 0.8 * (1 - t);
      ctx.fillStyle = theme.flash;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r * (0.6 + 0.7 * t), 0, Math.PI * 2);
      ctx.fill();
    } else if (e.kind === 'gold') {
      ctx.globalAlpha = 0.6 * (1 - t);
      ctx.fillStyle = theme.gold;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r * (1 + 2 * t), 0, Math.PI * 2);
      ctx.fill();
    } else if (e.kind === 'puff') {
      // Little cloud: soft white blobs drifting outward and fading.
      const ease = 1 - (1 - t) * (1 - t);
      ctx.fillStyle = theme.flash;
      for (const bl of e.blobs) {
        ctx.globalAlpha = 0.7 * (1 - t);
        ctx.beginPath();
        ctx.arc(e.x + bl.dx * ease, e.y + bl.dy * ease - 6 * ease,
          bl.r * (0.6 + 0.9 * t), 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (e.kind === 'particles') {
      ctx.globalAlpha = 1 - t;
      for (const p of e.parts) {
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x + p.vx * t * e.dur, p.y + p.vy * t * e.dur + 0.5 * 0.0008 * (t * e.dur) ** 2,
          p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (e.kind === 'popup') {
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = theme.accent;
      ctx.font = 'bold 28px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(e.text, e.x, e.y - 50 * t);
    }
    ctx.restore();
  }
}

// ── UI ────────────────────────────────────────────────────────────────────────
function drawButton(ctx: CanvasRenderingContext2D, b: Button, label: string, bg = theme.accent): void {
  ctx.fillStyle = bg;
  roundRect(ctx, b.x, b.y, b.w, b.h, 14);
  ctx.fill();
  ctx.fillStyle = theme.onAccent;
  ctx.font = 'bold 20px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, b.x + b.w / 2, b.y + b.h / 2 + 1);
  ctx.textBaseline = 'alphabetic';
}

function drawContainer(ctx: CanvasRenderingContext2D): void {
  const L = CONTAINER_LEFT, R = CONTAINER_RIGHT, T = CONTAINER_TOP, B = CONTAINER_BOTTOM;
  const rad = 24;
  // Interior fill (closed path, top edge implied).
  ctx.fillStyle = theme.containerIn;
  ctx.beginPath();
  ctx.moveTo(L, T);
  ctx.lineTo(L, B - rad);
  ctx.quadraticCurveTo(L, B, L + rad, B);
  ctx.lineTo(R - rad, B);
  ctx.quadraticCurveTo(R, B, R, B - rad);
  ctx.lineTo(R, T);
  ctx.closePath();
  ctx.fill();
  // Walls: sides + rounded bottom, top stays open.
  ctx.strokeStyle = theme.container;
  ctx.lineWidth = WALL_THICK;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(L, T);
  ctx.lineTo(L, B - rad);
  ctx.quadraticCurveTo(L, B, L + rad, B);
  ctx.lineTo(R - rad, B);
  ctx.quadraticCurveTo(R, B, R, B - rad);
  ctx.lineTo(R, T);
  ctx.stroke();
}

function drawDeathLine(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  ctx.globalAlpha = 0.6;
  ctx.strokeStyle = theme.death;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.moveTo(CONTAINER_LEFT, DEATH_LINE_Y);
  ctx.lineTo(CONTAINER_RIGHT, DEATH_LINE_Y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = theme.death;
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('GAME OVER', CONTAINER_RIGHT - 2, DEATH_LINE_Y - 4);
  ctx.restore();
}

function drawTopUI(ctx: CanvasRenderingContext2D, s: Scene): void {
  // Score / best (top-left)
  ctx.fillStyle = theme.textDim;
  ctx.font = '12px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('SCORE', 18, 26);
  ctx.fillStyle = theme.text;
  ctx.font = 'bold 30px system-ui, sans-serif';
  ctx.fillText(String(s.score), 18, 56);
  ctx.fillStyle = theme.textDim;
  ctx.font = '13px system-ui, sans-serif';
  ctx.fillText('Best: ' + s.best, 18, 78);

  // Next preview (top-right, under pause button)
  const nx = W - 33, ny = 90;
  ctx.fillStyle = theme.textDim;
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('NEXT', nx, 70);
  drawFruit(ctx, s.nextTier, nx, ny, 0, Math.min(18 / fruitRadius(s.nextTier), 1));

  drawButton(ctx, PAUSE_BTN, '⏸');
}

function drawDropper(ctx: CanvasRenderingContext2D, s: Scene): void {
  const x = s.dropperX;
  // Aim guide
  ctx.save();
  ctx.globalAlpha = 0.25;
  ctx.strokeStyle = theme.textDim;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 8]);
  ctx.beginPath();
  ctx.moveTo(x, DROPPER_Y + fruitRadius(s.currentTier));
  ctx.lineTo(x, CONTAINER_BOTTOM);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
  // Ghost of current fruit
  drawFruit(ctx, s.currentTier, x, DROPPER_Y, 0, 1, 0.4);
}

// ── Overlays ───────────────────────────────────────────────────────────────────
function dim(ctx: CanvasRenderingContext2D, alpha: number): void {
  ctx.fillStyle = theme.overlayBg.replace(/[\d.]+\)$/, alpha + ')');
  ctx.fillRect(0, 0, W, H);
}

function drawFruitChain(ctx: CanvasRenderingContext2D, cy: number): void {
  // Decorative evolution row scaled to fit
  const n = FRUITS.length;
  const gap = (W - 40) / n;
  for (let i = 0; i < n; i++) {
    const x = 20 + gap * (i + 0.5);
    const r = 6 + i * 1.1;
    ctx.fillStyle = FRUITS[i].color;
    ctx.beginPath();
    ctx.arc(x, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawMenu(ctx: CanvasRenderingContext2D, s: Scene): void {
  dim(ctx, 0.12);
  ctx.textAlign = 'center';
  ctx.fillStyle = theme.text;
  ctx.font = 'bold 48px system-ui, sans-serif';
  ctx.fillText('Fruit Drop', W / 2, 230);
  ctx.fillStyle = theme.textDim;
  ctx.font = '15px system-ui, sans-serif';
  ctx.fillText('Drop & merge fruits — make the watermelon!', W / 2, 262);
  drawFruitChain(ctx, 330);
  drawButton(ctx, getButtons('MENU').play, 'Play');
  if (s.best > 0) {
    ctx.fillStyle = theme.textDim;
    ctx.font = '16px system-ui, sans-serif';
    ctx.fillText('Best: ' + s.best, W / 2, 570);
  }
}

function drawPause(ctx: CanvasRenderingContext2D): void {
  dim(ctx, 0.5);
  ctx.textAlign = 'center';
  ctx.fillStyle = theme.overlayText;
  ctx.font = 'bold 40px system-ui, sans-serif';
  ctx.fillText('Paused', W / 2, 250);
  const b = getButtons('PAUSED');
  drawButton(ctx, b.resume, 'Resume');
  drawButton(ctx, b.restart, 'Restart');
  drawButton(ctx, b.home, 'В хаб', theme.death);
}

function drawGameOver(ctx: CanvasRenderingContext2D, s: Scene): void {
  const t = Math.min((s.now - s.overlayStartMs) / 300, 1);
  dim(ctx, 0.5 * Math.max(t, 0.6));
  ctx.textAlign = 'center';
  ctx.fillStyle = theme.overlayText;
  ctx.font = 'bold 44px system-ui, sans-serif';
  ctx.fillText('Game Over', W / 2, 250);
  ctx.font = '16px system-ui, sans-serif';
  ctx.fillStyle = theme.overlayText.replace(/[\d.]+\)$/, '0.7)');
  ctx.fillText('SCORE', W / 2, 300);
  ctx.fillStyle = theme.overlayText;
  ctx.font = 'bold 56px system-ui, sans-serif';
  ctx.fillText(String(s.score), W / 2, 352);
  ctx.font = '18px system-ui, sans-serif';
  ctx.fillStyle = theme.overlayText.replace(/[\d.]+\)$/, '0.8)');
  ctx.fillText('Best: ' + s.best, W / 2, 384);
  const b = getButtons('GAME_OVER');
  drawButton(ctx, b.again, 'Play Again');
  drawButton(ctx, b.menu, 'Menu');
}

// ── Main entry ───────────────────────────────────────────────────────────────
export function renderFrame(ctx: CanvasRenderingContext2D, s: Scene): void {
  ctx.save();
  ctx.translate(s.shakeX, s.shakeY);

  ctx.fillStyle = bgGrad;
  ctx.fillRect(-20, -20, W + 40, H + 40);

  drawContainer(ctx);
  if (s.phase !== 'MENU') drawDeathLine(ctx);

  // Fruits
  for (const b of fruitBodies()) {
    const f = b.plugin.fruit as FruitData;
    const scale = fruitScale(s.now - f.spawnMs, f.scaleFrom);
    drawFruit(ctx, f.tier, b.position.x, b.position.y, b.angle, scale);
  }

  if (s.phase === 'PLAYING') {
    drawDropper(ctx, s);
  }

  drawEffects(ctx, s.effects, s.now);

  if (s.phase !== 'MENU') drawTopUI(ctx, s);

  if (s.phase === 'MENU') drawMenu(ctx, s);
  else if (s.phase === 'PAUSED') drawPause(ctx);
  else if (s.phase === 'GAME_OVER') drawGameOver(ctx, s);

  ctx.restore();
}
